import { useMemo, useState, type ReactNode } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { useFreelancers } from "@/hooks/use-freelancers";
import { formatNOK } from "@/lib/format";
import { primaryDiscipline, ratingSummary, type FreelancerListRow } from "@/lib/freelancers";
import { rankFreelancersForLine } from "@/lib/freelancer-projects";
import { FreelancerFormDialog } from "./FreelancerFormDialog";

interface Props {
  /** Linjenavn eller leverandørnavn. Frilansere med fagfelt som ligner, vises først. */
  context: string;
  excludeIds?: string[];
  onPick: (freelancerId: string) => void;
  trigger: ReactNode;
  /** Forslag som vises øverst (f.eks. navnetreff på leverandør). */
  suggestedId?: string | null;
}

function ItemLabel({ row }: { row: FreelancerListRow }) {
  const { avg, count } = ratingSummary(row.ratings);
  const meta = [
    primaryDiscipline(row)?.label,
    row.day_rate_nok != null ? `${formatNOK(row.day_rate_nok)}/dag` : null,
    count > 0 ? `${avg?.toLocaleString("nb-NO")} ★` : "Ikke testet",
  ].filter(Boolean).join(" · ");
  return (
    <div className="min-w-0">
      <div className="truncate font-medium">{row.name}</div>
      <div className="truncate text-xs text-muted-foreground">{meta}</div>
    </div>
  );
}

/**
 * VIB-2927: velg frilanser fra banken. Dagspris og vurdering vises bare som
 * informasjon; valget skriver aldri kost. Kan opprette ny frilanser.
 */
export function FreelancerPicker({ context, excludeIds = [], onPick, trigger, suggestedId }: Props) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const { data: rows = [] } = useFreelancers();

  const ranked = useMemo(() => {
    const excluded = new Set(excludeIds);
    return rankFreelancersForLine(rows, context).filter((r) => !excluded.has(r.row.id));
  }, [rows, context, excludeIds]);
  const suggested = suggestedId ? ranked.find((r) => r.row.id === suggestedId) : undefined;
  const matching = ranked.filter((r) => r.matches && r !== suggested);
  const others = ranked.filter((r) => !r.matches && r !== suggested);

  const pick = (id: string) => {
    setOpen(false);
    onPick(id);
  };

  const group = (heading: string, items: typeof ranked) =>
    items.length > 0 && (
      <CommandGroup heading={heading}>
        {items.map(({ row }) => (
          <CommandItem key={row.id} value={`${row.name} ${row.company_name ?? ""} ${row.id}`} onSelect={() => pick(row.id)}>
            <ItemLabel row={row} />
          </CommandItem>
        ))}
      </CommandGroup>
    );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput placeholder="Søk frilanser…" aria-label="Søk frilanser" />
            <CommandList className="max-h-72">
              <CommandEmpty>Ingen treff</CommandEmpty>
              {suggested && group("Forslag", [suggested])}
              {group("Passer linjen", matching)}
              {group(matching.length > 0 || suggested ? "Andre" : "Frilansere", others)}
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value="__ny_frilanser"
                  onSelect={() => { setOpen(false); setCreateKey((k) => k + 1); setCreateOpen(true); }}
                >
                  <SrIcon name="plus" className="mr-2 h-4 w-4" />
                  Ny frilanser
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <FreelancerFormDialog key={createKey} open={createOpen} onOpenChange={setCreateOpen} onSaved={onPick} />
    </>
  );
}
